import React from 'react';
import {HORSE_RACE_CASUALTIES} from 'modules/Common/consts';
import { slugifyName } from 'utils';
import {localFullDateShort, localDateTimeShort, localTimeShort} from 'utils/LocalDateTimeHelper';
import {getWidgetValues} from "sportinglife-react-common/widgets";

const RACE_RESULT_STATUSES = {
  // before
  DORMANT:       "Scheduled",
  DELAYED:       "Delayed",
  PARADING:      "Parading",
  GOINGDOWN:     "Going Down",
  ATTHEPOST:     "At The Post",
  GOINGBEHIND:   "Going Behind",
  GOINGINSTALLS: "Going In Stalls",
  UNDERORDERS:   "Under Orders",
  OFF:           "Running",
  FALSESTART:    "False Start",
  RACEVOID:      "Race Void",
  ABANDONED:     "Abandoned",

  // after
  FINISHED:      "Finished",
  PHOTOGRAPH:    "Photograph",
  RESULT:        "Result",
  WEIGHEDIN:     "Weighed In",
  AMENDEDRESULT: "Amended Result"
};

function isFinishedRaceStatus(stage) {
  return ['FINISHED', 'PHOTOGRAPH', 'RESULT', 'WEIGHEDIN'].indexOf(stage) !== -1;
}

export function isBeforeOffStatus(stage) {
  return ["DORMANT", "DELAYED", "PARADING", "GOINGDOWN", "ATTHEPOST", "GOINGBEHIND", "GOINGINSTALLS", "UNDERORDERS"].indexOf(stage) !== -1;
}

export function raceStatusLabel(status, stewards) {
  return RACE_RESULT_STATUSES[stewards] || RACE_RESULT_STATUSES[status] || status;
}

export function transformCasualty(casualty, defaultCode = "DNF") {
  return HORSE_RACE_CASUALTIES[casualty] || defaultCode;
}

export function keepUpperCase(s) {
  return s.replace(/[^A-Z]/g, '');
}

export function formatHorseAge(age) {
  return (age || '').toLowerCase().replace(' only', '').replace(' plus', '+');
}

export function distanceFormat(dist) {
  return (dist || '').replace(/^0m | 0f| 0y/g, "");
}

export function formatHeadgear(headgears) {
  return headgears.filter(h => h.symbol && h.symbol.length > 0)
    .map(h => `${h.symbol}${h.count === 1 ? '1' : ''}`)
    .join();
}

export function formatPosition(runner, {defaultPosition = "-", defaultCasualtyCode, showRunnerCount = true} = {}) {
  let positionString;

  const {
    casualty: {
      reason: casualty
    },
    position,
    amended_position,
    runner_count
  } = runner;

  if (amended_position || position) {
    positionString = amended_position || position;

    if (showRunnerCount && runner_count) {
      positionString = `${positionString}/${runner_count}`;
    }
  }
  else if (casualty) {
    positionString = transformCasualty(casualty, defaultCasualtyCode);
  }
  else {
    positionString = defaultPosition;
  }

  return positionString;
}

export function formatRaceDetails(result) {
  const details = [];
  if (result.course_name) {
    details.push({text: result.course_name, strong: true});
  }

  if (result.distance) {
    details.push({text: result.distance});
  }

  if (result.going) {
    details.push({text: result.going});
  }

  if (result.race_class) {
    details.push({text: `C${result.race_class}`});
  }

  return details.map((d, i) => (d.strong ? <strong key={i}>{d.text}</strong> : <span key={i}>{i?", ":""}{d.text}</span>));
}

export function racecardHref({
  race_id,
  race_name = "unknown",
  date,
  course_name
}) {
  return `/racing/racecards/${date}/${slugifyName(course_name)}/racecard/${race_id}/${slugifyName(race_name)}`;
}

export function resultHref({
    race_id,
    race_name = "unknown",
    date,
    course_name
  }) {
  return `/racing/results/${date}/${slugifyName(course_name)}/${race_id}/${slugifyName(race_name)}`;
}

export function racecardResultHref(raceDetails) {
  const {
    raceStage
  } = raceDetails;

  return isFinishedRaceStatus(raceStage) ? resultHref(raceDetails) : racecardHref(raceDetails);
}

export function getProfileHelpers(profile, earlyEntries) {
  let dataKey, formatDateTime, buildHref;

  if (earlyEntries) {
    dataKey = "future_entries";
    formatDateTime = result => localDateTimeShort(`${result.date} ${result.time}`);
    buildHref = racecardHref;
  }
  else {
    dataKey = "previous_results";
    formatDateTime = result => localFullDateShort(result.date);
    buildHref = resultHref;
  }

  const {
    [dataKey] : results = []
  } = profile;

  return { results, formatDateTime, buildHref };
}

export function racingProfileLink(profileType, id) {
  return id && `/racing/profiles/${profileType}/${id}`;
}

export function rideDetailsFromEntry(entry) {
  const {
    ride: {
      ride_reference: {
        external_reference: externalRideReference
      },
      horse: {
        name: horseName
      },
      betting: {
        current_odds: odds,
        market_reference: {
          external_reference: [
            {
              value: marketId
            } = {}
          ] = []
        } = {}
      },
      has_sp: startingPriceAvailable
    },
    race: {
      race_summary: {
        race_summary_reference: {
          external_reference: externalRaceReference,
          id: raceId
        },
        date: raceDate,
        time: raceTime,
        course_name: courseName,
        race_stage: raceStage,
        name: raceName
      },
      each_way: eachWay,
      market_suspended: suspended,
      fixture
    }
  } = entry;

  const betlinkIds = externalRideReference.filter(r => r.ref === 'skybet').map(r => r.value);
  const raceSkyBetIds = externalRaceReference.filter(r => r.ref === 'skybet').map(r => r.value);
  const raceExternalId = raceSkyBetIds.length > 0 ? raceSkyBetIds[0] : '';
  const displayTime = localTimeShort(`${raceDate}T${raceTime}Z`);
  const eventName = `${displayTime} ${courseName}`;

  let betlinkProps;
  if (odds) {
    betlinkProps = {
      marketId: marketId,
      eventId: raceExternalId,
      eventName,
      eventCompetition: courseName, // only relevant for feature "integrated-betslip"
      dateTime: `${raceDate} ${raceTime}`,
      sport: "horseRacing",
      betId: betlinkIds[0],
      betName: horseName,
      odds,
      eachWay, // only relevant for feature "integrated-betslip"
      suspended, // only relevant for feature "integrated-betslip"
      fixture, // only relevant for feature "integrated-betslip"
      startingPriceAvailable // only relevant for feature "integrated-betslip"
    };
  }

  const raceLinkDetails = {
    raceStage,
    eventName,
    race_id: raceId,
    race_name: raceName,
    date: raceDate,
    course_name: courseName
  };

  return {raceLinkDetails, betlinkProps};
}

export function buildRuleFourString(deduction) {
    let type;
    switch(deduction.type) {
      case "BoardPrices":
        type = "board prices only";
        break;
      case "AllBets":
        type = "all bets";
        break;
      default:
        return "No rule 4 in effect"; // Only displayed if illegal value was returned
    }
    return `Rule 4 applies to ${type} - deduction ${deduction.value}p in the pound`;
}

export function transformedRideDataFromArticleWidget(widget, races) {
  const {ride_id, race_id} = getWidgetValues(widget, ["ride_id", "race_id"]);

  if (!ride_id || !race_id) {
    return;
  }

  if (!races) {
    return;
  }

  const [raceData] = races.filter(({race_summary: {race_summary_reference: {id} = {}} = {}} = {}) =>( id == race_id));

  if (!raceData) {
    return;
  }

  const [rideData] = raceData.rides.filter(({ride_reference: {id} = {}} = {}) => (id == ride_id));

  if (!rideData) {
    return;
  }

  const {
    race_summary: raceSummary,
    race_summary: {
      race_summary_reference = {}
    } = {},
    each_way: eachWay,
    fixture,
    market_suspended: suspended
  } = raceData;

  const {
    ride_status: status,
    cloth_number: saddleClothNo,
    draw_number: drawNumber,
    amended_position,
    finish_position,
    horse,
    horse: {
      formsummary: {
        display_text: form
      } = {},
      name: horseName,
      last_ran_days: horseLastRan,
      age: horseAge,
      last_run_video
    } = {},
    formwatch: formWatch,
    headgear,
    race_history_stats: raceHistoryStats,
    weight_penalty: weightPenalty,
    handicap: weight,
    ride_description: rideDesc,
    ride_reference,
    trainer,
    jockey,
    jockey_claim,
    official_rating: officialRating,
    betting: {
      current_odds: odds,
      market_reference
    } = {},
    silk_filename: silkFilename,
    has_sp: startingPriceAvailable
  } = rideData;


  return {
    runner: {
      key: ride_id,
      status,
      saddleClothNo,
      drawNumber,
      position : `${amended_position || finish_position} ${bracket(drawNumber)}`,
      form,
      commentary: "", // not used by embedded card
      formWatch,
      headgear,
      raceHistoryStats,
      weightPenalty,
      horse,
      horseName,
      horseLastRan,
      horseAge,
      lastRunVideo: last_run_video ? [last_run_video] : [],
      weight,
      rideDesc,
      rideBetlinkIds: [getSkyRef(ride_reference)],
      trainer,
      jockey,
      jockeyClaim: bracket(jockey_claim),
      officialRating,
      odds,
      marketId: getSkyRef(market_reference),
      silkFilename,
      startingPriceAvailable
    },
    raceSummary,
    eventRef: getSkyRef(race_summary_reference),
    eachWay,
    fixture,
    suspended
  }

}

export function bracket(t) {
  return t ? `(${t})` : '';
}

export function getSkyRef({external_reference = []} = {}) {
  const [{value} = {}] = external_reference.filter(r => r.ref === 'skybet');
  return value;
}



// WEBPACK FOOTER //
// ./modules/HorseRacing/utils/index.js